import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ProjectSubTypeId, ProjectTypeId } from '@app/shared/data/project-enums';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { PlanningTaskStatus } from '@app/shared/models/planning-task-status';
import { DatePickerComponent } from '@progress/kendo-angular-dateinputs';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { ErrorService } from 'app/common/services/error.service';
import moment from 'moment';
import { finalize, Subscription } from 'rxjs';
import { PlanningTaskData, ProjectPlanningData } from '../../models/project-planning-data';
import { GeneralProjectService } from '../../services/general-project.service';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectObserverService } from '../../services/project-observer.service';
import { ProjectBaseScenarioTabItemComponent } from '../project-base-scenario-tabitem.component';
import { PlanningTasksGridOptions, PlanningTasksGridOptionsService } from './columns';

interface Contractor {
  ContractorId: number;
  Name: string;
}

@Component({
  selector: 'app-project-planning',
  templateUrl: './project-planning.component.html',
  styleUrls: ['./project-planning.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ComboBoxComponent,
    DatePickerComponent,
    DisabledControlDirective,
    FormsModule,
    LoaderComponent,
    ReactiveFormsModule,
    TextBoxComponent,
    TooltipDirective,
  ],
})
export class ProjectPlanningComponent extends ProjectBaseScenarioTabItemComponent implements OnInit, AfterViewInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private generalProjectService = inject(GeneralProjectService);
  private lookupPlusService = inject(LookupPlusService);
  private planningTasksGridOptionService = inject(PlanningTasksGridOptionsService);
  private poSvc = inject(ProjectObserverService);
  private errorService = inject(ErrorService);
  private destroyRef = inject(DestroyRef);

  private gridApi: GridApi;

  readonly tabTitle = output<string>();
  readonly tabTdv = output<boolean>();

  private subscribe?: Subscription;
  private gridOptionQueue: Array<() => void> = [];
  public projectPlanningForm: UntypedFormGroup;
  public planningData: ProjectPlanningData;
  public planningStatuses: PlanningTaskStatus[];
  public loading: boolean = false;
  public planningTasksGridOptions: PlanningTasksGridOptions;
  public editedTasks: Map<number, PlanningTaskData> = new Map<number, PlanningTaskData>();
  public dContractor = new Array<Contractor>();
  public canEditNeedByDate: boolean;
  public isTBProject: boolean;
  public isSWDFacilityProject: boolean;

  constructor() {
    super();
    this.createForm();
    this.planningTasksGridOptions = this.planningTasksGridOptionService.planningTasksGridOptions;
  }

  ngAfterViewInit() {
    this.poSvc.projectObserve('planning', this.projectPlanningForm.valid);
  }

  ngOnInit() {
    this.loading = true;

    this.setFieldAttributes();

    void Promise.allSettled([
      this.loadPlanningTaskStatuses(this.projectTab().ProjectTypeId),
      this.loadPlanningTasks(),
      this.loadContractors()
    ])
      .then(() => {
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.errorService.error('Error loading project planning data', error);
      });
  }

  ngOnDestroy() {
    this.subscribe?.unsubscribe();
  }

  get formPlanningTasks(): UntypedFormArray {
    return this.projectPlanningForm.get('FormPlanningTasks') as UntypedFormArray;
  }

  public createForm(): void {
    this.projectPlanningForm = this.formBuilder.group({
      ProjectId: [null],
      ConstructionStart: [null],
      ConstructionEnd: [null],
      EarliestRigRelease: [null],
      EarliestPadRigRelease: [null],
      EarliestPOP: [null],
      DirtWorkContractorId: [null],
      ElectricalWorkContractorId: [null],
      MechanicalContractor: [null],
      TargetCompleteDate: [null],
      FormPlanningTasks: this.formBuilder.array([]),
      PlanningTasks: this.formBuilder.array([])
    });
  }

  public savePlanningTasks(e: any): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    let editedData = (this.projectPlanningForm.get('FormPlanningTasks')?.value ?? []).concat(
      Array.from(this.editedTasks.values()));

    let dataToSave = {
      ProjectId: this.projectTab().ProjectId,
      DirtWorkContractorId: this.projectPlanningForm.get('DirtWorkContractorId')?.value,
      ElectricalWorkContractorId: this.projectPlanningForm.get('ElectricalWorkContractorId')?.value,
      TargetCompleteDate: this.projectPlanningForm.get('TargetCompleteDate')?.value,
      PlanningTasks: editedData
    };

    this.generalProjectService.updateProjectPlanningTasks(dataToSave)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        complete: () => {
          this.editedTasks.clear();
          this.refreshData();
        },
        error: (error) => {
          this.errorService.error('Error saving project planning data', error);
        }
      });
  }

  public cancelChanges(e: any): void {
    this.editedTasks.clear();
    this.refreshData();
  }

  public onRowValueChanged(e: any): void {
    this.trackChanges(e);
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridOptionQueue.forEach(func => func());
    this.gridOptionQueue = [];
  }

  private trackChanges(e: any): void {
    if (e.data.ActualCompleteDate != null)
      e.data.ActualCompleteDate = moment(e.data.ActualCompleteDate).toDate();

    this.editedTasks.set(e.data.ProjectPlanningTaskId, e.data);
    this.tabTdv.emit(true);
  }

  private refreshData(): void {
    this.subscribe?.unsubscribe();
    this.loadPlanningTasks();
    this.tabTdv.emit(false);
  }

  private loadContractors(): Promise<void> {
    return this.lookupPlusService.getLookup('Contractors', '?$select=ContractorId%2C%20Name')
      .then(payload => {
        this.dContractor = payload.value;
      });
  }

  private loadPlanningTaskStatuses(projectTypeId: number): Promise<void> {
    return this.generalProjectService.getPlanningTaskStatuses(projectTypeId)
      .then((resp) => {
        this.planningStatuses = resp;
        this.planningTasksGridOptionService.PlanningTaskStatus = new Map(resp.map(t => [t.PlanningTaskStatusId, t]));
      });
  }

  private loadPlanningTasks(): Promise<void> {
    return this.generalProjectService.getProjectPlanningData(this.projectTab().ProjectId, this.projectTab().ProjectTypeId)
      .then((resp) => {
        this.planningData = resp;

        this.actualizeDates(this.planningData, ['ConstructionStart', 'ConstructionEnd', 'EarliestRigRelease', 'EarliestPadRigRelease', 'EarliestPOP', 'TargetCompleteDate']);
        this.actualizeArrayDates(this.planningData.PlanningTasks, ['ActualCompleteDate', 'ActualStartDate', 'TargetCompletionDate']);

        this.projectPlanningForm.patchValue(this.planningData);

        const planningTaskArray = this.formBuilder.array(this.planningData.PlanningTasks.map(x => this.formBuilder.group(x)));//.filter(x => x.PlanningTaskId !== 1 && x.PlanningTaskId !== 2)
        this.projectPlanningForm.setControl('PlanningTasks', planningTaskArray);

        this.setGridOption(() => this.gridApi.setGridOption('rowData', this.projectPlanningForm.get('PlanningTasks')?.value));

        this.subscribe = this.projectPlanningForm.valueChanges.subscribe(() => {
          this.tabTdv.emit(true);
        });
      })
      .catch((error) => {
        this.errorService.error('Error loading project planning data', error);
      });
  }

  private setGridOption(func: () => void): void {
    if (this.gridApi) {
      func();
    } else {
      this.gridOptionQueue.push(func);
    }
  }

  private setFieldAttributes(): void {
    this.isTBProject = this.projectTab().ProjectTypeId == ProjectTypeId.TankBattery;
    this.isSWDFacilityProject = this.projectTab().ProjectTypeId == ProjectTypeId.SaltWaterDisposal;

    this.canEditNeedByDate = (this.isTBProject && this.projectTab().ProjectSubTypeId == ProjectSubTypeId.Compressor) || this.isSWDFacilityProject;
  }

  private actualizeDates(x: any, fields: Array<string>): void {
    fields.forEach(field => {
      x[field] = x[field] != null ? moment(x[field]).startOf('day').toDate() : null;
    });
  }

  private actualizeArrayDates(x: any, fields: Array<string>): void {
    fields.forEach(field => {
      x.forEach(element => {
        element[field] = element[field] != null ? moment(element[field]).startOf('day').toDate() : null;
      });
    });
  }
}